export type ProcessStep = { step: string; title: string; desc: string };
export type WhenItem = { icon: string; title: string; desc: string };
export type ChooseItem = { criterion: string; explain: string };
export type CertItem = { name: string; desc: string };
export type FaqItem = { q: string; a: string };

export type MetierData = {
  slug: string;
  profession: string;
  singular: string;
  plural: string;
  preposition: string;
  heroTitle: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  benefits: { title: string; desc: string }[];
  heroImage: string;
  emoji: string;
  processSteps: ProcessStep[];
  whenToConsult: WhenItem[];
  howToChoose: ChooseItem[];
  certifications: CertItem[];
  relatedMetiers: string[];
  faq: FaqItem[];
};

export const METIERS_DATA: Record<string, MetierData> = {
  'educateur-canin': {
    slug: 'educateur-canin',
    profession: 'trainers',
    singular: 'éducateur canin',
    plural: 'éducateurs canins',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un RDV avec un éducateur canin',
    metaTitle: 'Éducateur canin. Réservez en ligne sur Rintintin',
    metaDescription:
      'Trouvez et réservez un éducateur canin près de chez vous. Dressage, obéissance, éducation du chiot. Avis clients, réservation immédiate sur Rintintin.',
    intro:
      "Un éducateur canin vous accompagne pour poser les bases d'une relation sereine avec votre chien. De l'éducation du chiot aux problèmes plus spécifiques, il adapte ses méthodes à votre compagnon.",
    benefits: [
      {
        title: 'Profils détaillés',
        desc: 'Consultez la description, les tarifs et les avis clients de chaque éducateur avant de réserver.',
      },
      {
        title: 'Méthodes variées',
        desc: 'Chaque éducateur a son approche : consultez son profil et ses avis pour choisir en connaissance de cause.',
      },
      {
        title: 'Près de chez vous',
        desc: 'À domicile, en extérieur ou dans des espaces dédiés selon le pro.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1600&q=70',
    emoji: '🎓',
    processSteps: [
      {
        step: '01',
        title: 'Premier contact',
        desc: 'Échange sur vos objectifs, l’historique du chien et votre mode de vie.',
      },
      {
        step: '02',
        title: 'Bilan comportemental',
        desc: 'Observation du chien en situation réelle pour identifier les priorités.',
      },
      {
        step: '03',
        title: 'Plan personnalisé',
        desc: 'Séances ciblées : rappel, marche en laisse, socialisation, etc.',
      },
      {
        step: '04',
        title: 'Suivi & progrès',
        desc: 'Exercices à refaire entre les séances, ajustements selon les progrès.',
      },
    ],
    whenToConsult: [
      {
        icon: '🐶',
        title: 'Chiot de 2 à 16 semaines',
        desc: 'Période de socialisation cruciale : bases, propreté, habituation.',
      },
      {
        icon: '🦮',
        title: 'Chien qui tire en laisse',
        desc: 'Promenades difficiles, frustration des deux côtés du fil.',
      },
      {
        icon: '🔊',
        title: 'Rappel inexistant',
        desc: 'Le chien ne revient pas ou ignore les ordres en extérieur.',
      },
      {
        icon: '👥',
        title: 'Peur des humains ou chiens',
        desc: 'Évitement, aboiements, retrait : besoin de désensibilisation.',
      },
      {
        icon: '🏠',
        title: 'Propreté non acquise',
        desc: 'Accidents réguliers malgré les efforts à la maison.',
      },
      {
        icon: '🎾',
        title: 'Hyperactivité',
        desc: 'Difficulté à se calmer, destruction, demandes d’attention constantes.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Diplôme et ACACED',
        explain:
          'Vérifiez la formation initiale et le certificat de capacité obligatoire en France.',
      },
      {
        criterion: 'Méthodes utilisées',
        explain:
          'Privilégiez le renforcement positif ; fuyez les méthodes coercitives ou punitives.',
      },
      {
        criterion: 'Spécialisation',
        explain: 'Chiot, rééducation, sport canin : certains pros ont une expertise ciblée.',
      },
      {
        criterion: 'Avis et références',
        explain: 'Consultez les retours d’autres propriétaires sur Rintintin avant de réserver.',
      },
    ],
    certifications: [
      {
        name: 'ACACED',
        desc: 'Attestation de connaissances pour les animaux de compagnie d’espèces domestiques, obligatoire pour exercer.',
      },
      {
        name: 'Brevet professionnel',
        desc: 'Formation d’éducateur canin reconnue par l’État (BP, DEJEPS, etc.).',
      },
      {
        name: 'Formation continue',
        desc: 'Spécialisations en comportement, chiens réactifs, chiots, sport canin.',
      },
    ],
    relatedMetiers: ['comportementaliste-canin', 'promeneur-canin'],
    faq: [
      {
        q: 'À quel âge commencer l’éducation d’un chiot ?',
        a: 'Dès 2 mois après la vaccination, pour la socialisation. Les bases (propreté, solitude) peuvent commencer à l’arrivée à la maison.',
      },
      {
        q: 'Combien de séances faut-il pour un bon rappel ?',
        a: 'Cela dépend du chien et de votre assiduité aux exercices. En général, 5 à 10 séances avec travail quotidien à la maison.',
      },
      {
        q: 'Éducateur ou comportementaliste : quelle différence ?',
        a: 'L’éducateur enseigne des comportements (obéissance, marche). Le comportementaliste traite les troubles profonds (anxiété, agressivité).',
      },
      {
        q: 'Les séances se font-elles à domicile ?',
        a: 'Oui, beaucoup d’éducateurs proposent des séances à domicile, en parc ou dans un centre selon le pro.',
      },
      {
        q: 'Faut-il être présent pendant les séances ?',
        a: 'Oui, votre présence est essentielle : vous apprenez à communiquer avec votre chien pour reproduire les exercices.',
      },
      {
        q: 'Un éducateur peut-il travailler avec un chien adulte ?',
        a: 'Absolument. Il n’est jamais trop tard pour apprendre, même si cela demande parfois plus de patience.',
      },
    ],
  },
  'comportementaliste-canin': {
    slug: 'comportementaliste-canin',
    profession: 'behaviorist',
    singular: 'comportementaliste canin',
    plural: 'comportementalistes canins',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un RDV avec un comportementaliste canin',
    metaTitle: 'Comportementaliste canin. Réservez en ligne sur Rintintin',
    metaDescription:
      'Consultez un comportementaliste canin pour résoudre les troubles du comportement de votre chien. Anxiété, agressivité, peurs. Réservation en ligne sur Rintintin.',
    intro:
      'Un comportementaliste décode les comportements de votre chien et vous aide à résoudre les problèmes profonds : anxiété de séparation, agressivité, peurs, fugues. Un travail patient sur la relation.',
    benefits: [
      {
        title: 'Diagnostic approfondi',
        desc: 'Évaluation complète du chien et de son environnement.',
      },
      { title: 'Approche bienveillante', desc: 'Comprendre avant de corriger, toujours.' },
      { title: 'Suivi personnalisé', desc: 'Un plan adapté à votre chien et à son histoire.' },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1600&q=70',
    emoji: '🧠',
    processSteps: [
      {
        step: '01',
        title: 'Anamnèse complète',
        desc: 'Questionnaire détaillé : antécédents, déclencheurs, contexte familial.',
      },
      {
        step: '02',
        title: 'Observation terrain',
        desc: 'Séance à domicile ou en situation déclenchante pour comprendre le mécanisme.',
      },
      {
        step: '03',
        title: 'Protocole sur mesure',
        desc: 'Plan de désensibilisation et contre-conditionnement progressif.',
      },
      {
        step: '04',
        title: 'Accompagnement long terme',
        desc: 'Séances de suivi pour ajuster le protocole selon les progrès.',
      },
    ],
    whenToConsult: [
      {
        icon: '😰',
        title: 'Anxiété de séparation',
        desc: 'Destruction, aboiements, salivation dès que vous partez.',
      },
      {
        icon: '😠',
        title: 'Agressivité',
        desc: 'Envers humains, chiens ou objets : grognements, morsures.',
      },
      {
        icon: '😨',
        title: 'Phobies',
        desc: 'Peur des orages, feux d’artifice, véhicules, sols glissants.',
      },
      {
        icon: '🏃',
        title: 'Fugue',
        desc: 'Le chien s’échappe systématiquement dès qu’il le peut.',
      },
      {
        icon: '💤',
        title: 'Comportements compulsifs',
        desc: 'Léchage excessif, tourner sur soi, poursuite de la queue.',
      },
      {
        icon: '🚫',
        title: 'Inhibition ou repli',
        desc: 'Chien qui se cache, refuse les contacts, tremble.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Formation spécialisée',
        explain: 'Comportementalisme ≠ éducation : vérifiez une formation dédiée aux troubles.',
      },
      {
        criterion: 'Approche éthologique',
        explain: 'Le pro doit chercher la cause, plutôt que de seulement masquer le symptôme.',
      },
      {
        criterion: 'Collaboration vétérinaire',
        explain: 'Pour les cas médicaux, un comportementaliste sérieux travaille avec un véto.',
      },
      {
        criterion: 'Transparence sur la durée',
        explain: 'Les troubles profonds demandent du temps : méfiez-vous des promesses miracles.',
      },
    ],
    certifications: [
      {
        name: 'ACACED',
        desc: 'Base obligatoire pour tout professionnel travaillant avec des chiens en France.',
      },
      {
        name: 'Master / DU comportement',
        desc: 'Formations universitaires ou diplômes spécialisés en éthologie appliquée.',
      },
      {
        name: 'Membre associations',
        desc: 'GECAF, Association Française des Comportementalistes, etc.',
      },
    ],
    relatedMetiers: ['educateur-canin', 'osteopathe-canin'],
    faq: [
      {
        q: 'Combien de temps pour voir des résultats ?',
        a: 'Les premiers signes d’amélioration apparaissent souvent en 4 à 8 semaines avec un protocole suivi à la lettre.',
      },
      {
        q: 'Un comportementaliste peut-il « guérir » l’agressivité ?',
        a: 'Il ne s’agit pas de guérir mais de gérer et réduire les réactions via un travail de fond sur les déclencheurs.',
      },
      {
        q: 'Faut-il une ordonnance vétérinaire ?',
        a: 'Non pour consulter. En revanche, un bilan vétérinaire est recommandé pour écarter une cause médicale.',
      },
      {
        q: 'Mon chien est-il trop vieux pour être aidé ?',
        a: 'Non. Les chiens seniors peuvent progresser, même si le rythme est plus lent.',
      },
      {
        q: 'Que faire en cas de morsure ?',
        a: 'Consultez rapidement un comportementaliste et un vétérinaire. Ne punissez pas le chien : cela aggrave souvent le problème.',
      },
      {
        q: 'Combien de séances sont nécessaires ?',
        a: 'Variable : de 3 séances pour un trouble léger à un suivi sur plusieurs mois pour des cas complexes.',
      },
    ],
  },
  'pet-sitter': {
    slug: 'pet-sitter',
    profession: 'pet-sitter',
    singular: 'pet-sitter',
    plural: 'pet-sitters',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un pet-sitter pour votre chien',
    metaTitle: 'Pet-sitter pour chien. Réservez en ligne sur Rintintin',
    metaDescription:
      'Faites garder votre chien à domicile ou chez un pet-sitter professionnel. Avis clients, réservation immédiate sur Rintintin.',
    intro:
      "Un pet-sitter prend soin de votre chien pendant vos absences, à votre domicile ou au sien. Plus qu'une garde, c'est une présence chaleureuse pour votre compagnon.",
    benefits: [
      {
        title: 'À domicile ou pension',
        desc: 'Vous choisissez la formule qui rassure le plus votre chien.',
      },
      {
        title: 'Compte-rendus quotidiens',
        desc: 'Photos et nouvelles régulières pendant votre absence.',
      },
      {
        title: 'Avis clients',
        desc: 'Lisez les retours de vrais propriétaires avant de réserver.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?auto=format&fit=crop&w=1600&q=70',
    emoji: '🏠',
    processSteps: [
      {
        step: '01',
        title: 'Prise de contact',
        desc: 'Échange sur les besoins du chien, durée, habitudes alimentaires.',
      },
      {
        step: '02',
        title: 'Rencontre préalable',
        desc: 'Visite de présentation pour que chien et pet-sitter se connaissent.',
      },
      {
        step: '03',
        title: 'Garde',
        desc: 'Visites à domicile ou hébergement chez le pet-sitter selon la formule.',
      },
      {
        step: '04',
        title: 'Bilan de fin',
        desc: 'Compte-rendu détaillé à votre retour : comportement, repas, sorties.',
      },
    ],
    whenToConsult: [
      {
        icon: '✈️',
        title: 'Vacances',
        desc: 'Week-end ou plusieurs semaines : garde adaptée à la durée.',
      },
      {
        icon: '💼',
        title: 'Déplacements pro',
        desc: 'Absences régulières en journée ou sur plusieurs jours.',
      },
      {
        icon: '🏥',
        title: 'Hospitalisation',
        desc: 'Quand vous ne pouvez pas assurer les soins quotidiens.',
      },
      {
        icon: '🏠',
        title: 'Chien anxieux seul',
        desc: 'Visites régulières pour rassurer un chien qui supporte mal la solitude.',
      },
      {
        icon: '💊',
        title: 'Soins spécifiques',
        desc: 'Administration de médicaments, régime particulier.',
      },
      {
        icon: '🐕',
        title: 'Plusieurs chiens',
        desc: 'Garde de plusieurs animaux dans le même foyer.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Formule adaptée',
        explain:
          'Visite à domicile (votre chien reste chez lui) ou garde chez le pet-sitter professionnel.',
      },
      {
        criterion: 'Expérience canine',
        explain: 'Privilégiez un pro habitué aux chiens, y compris au-delà des chats.',
      },
      {
        criterion: 'Assurance RC Pro',
        explain: 'Vérifiez que le pet-sitter est assuré en responsabilité civile professionnelle.',
      },
      {
        criterion: 'Contrat clair',
        explain: 'Tarifs, annulation, urgences : tout doit être écrit avant la garde.',
      },
    ],
    certifications: [
      {
        name: 'ACACED',
        desc: 'Obligatoire pour toute personne gardant des animaux à titre professionnel (art. L214-6 code rural).',
      },
      {
        name: 'Premiers secours canins',
        desc: 'Formation pour réagir en cas d’urgence pendant la garde.',
      },
      {
        name: 'Assurance RC Pro',
        desc: 'Protection en cas d’accident ou de dommage pendant la prestation.',
      },
    ],
    relatedMetiers: ['promeneur-canin', 'toiletteur-canin'],
    faq: [
      {
        q: 'Garde à domicile ou chez le pet-sitter : que choisir ?',
        a: 'À domicile : votre chien reste dans son environnement. Chez le pet-sitter professionnel : plus de présence humaine et de compagnie.',
      },
      {
        q: 'Combien de visites par jour pour une garde à domicile ?',
        a: 'En général 2 à 3 visites (repas, sortie, jeux). À définir avec le pet-sitter selon les besoins.',
      },
      {
        q: 'Mon chien peut-il rester avec d’autres chiens ?',
        a: 'Si le pet-sitter propose la garde en groupe et que votre chien est sociable, oui. Sinon, garde individuelle.',
      },
      {
        q: 'Que se passe-t-il en cas d’urgence vétérinaire ?',
        a: 'Un bon pet-sitter a un véto de garde et vous contacte immédiatement.',
      },
      {
        q: 'Faut-il fournir la nourriture ?',
        a: 'En général oui, pour garder les habitudes alimentaires du chien. À préciser à la réservation.',
      },
      {
        q: 'Comment préparer la première garde ?',
        a: 'Laissez les coordonnées vétérinaires, les clés, les instructions écrites et prévoyez une rencontre préalable.',
      },
    ],
  },
  'promeneur-canin': {
    slug: 'promeneur-canin',
    profession: 'dog-walker',
    singular: 'promeneur canin',
    plural: 'promeneurs canins',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un promeneur pour votre chien',
    metaTitle: 'Promeneur canin. Réservez en ligne sur Rintintin',
    metaDescription:
      'Trouvez un promeneur canin de confiance pour des sorties régulières. Solo ou en petit groupe, urbain ou nature. Réservation simple sur Rintintin.',
    intro:
      "Un promeneur canin offre à votre chien la sortie qu'il mérite quand vous travaillez. Sorties solo pour les chiens timides, en petits groupes pour les sociables : chaque promenade est adaptée.",
    benefits: [
      { title: 'Solo ou collectif', desc: 'Selon le tempérament et les besoins de votre chien.' },
      { title: 'Horaires flexibles', desc: 'Promenades ponctuelles ou abonnement régulier.' },
      {
        title: 'Compte-rendu après chaque sortie',
        desc: 'Message ou photo en fin de promenade pour vous tenir informé.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1600&q=70',
    emoji: '🚶',
    processSteps: [
      {
        step: '01',
        title: 'Première rencontre',
        desc: 'Présentation du chien, test de la laisse, repérage du quartier.',
      },
      {
        step: '02',
        title: 'Réservation',
        desc: 'Créneaux ponctuels ou forfait hebdomadaire via Rintintin.',
      },
      {
        step: '03',
        title: 'Promenade',
        desc: '30 à 60 min selon la formule : besoins, jeu, socialisation.',
      },
      {
        step: '04',
        title: 'Compte-rendu',
        desc: 'Message ou photo à la fin de la sortie pour vous rassurer.',
      },
    ],
    whenToConsult: [
      {
        icon: '💼',
        title: 'Travail à temps plein',
        desc: 'Votre chien reste seul toute la journée sans sortie.',
      },
      {
        icon: '⚡',
        title: 'Chien très actif',
        desc: 'Berger, malinois, husky : besoin de dépense physique importante.',
      },
      {
        icon: '🏢',
        title: 'Appartement sans jardin',
        desc: 'Pas d’espace extérieur pour les besoins et l’exercice.',
      },
      {
        icon: '🩹',
        title: 'Convalescence légère',
        desc: 'Sorties courtes et douces pendant une rééducation.',
      },
      {
        icon: '👴',
        title: 'Propriétaire peu mobile',
        desc: 'Quand vous ne pouvez plus assurer les longues promenades.',
      },
      {
        icon: '🌧️',
        title: 'Météo difficile',
        desc: 'Un pro sort votre chien même quand vous ne pouvez pas.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Taille du groupe',
        explain: 'Solo pour chiens réactifs ou timides ; petit groupe (3-4 max) pour sociabiliser.',
      },
      {
        criterion: 'Zone de promenade',
        explain: 'Parc, forêt, urbain : choisissez selon les besoins de dépense de votre chien.',
      },
      {
        criterion: 'Assurance et sérieux',
        explain: 'RC Pro, ponctualité, communication : lisez les avis sur Rintintin.',
      },
      {
        criterion: 'Forfait ou à la séance',
        explain:
          'Les forfaits hebdomadaires sont souvent plus économiques pour un besoin régulier.',
      },
    ],
    certifications: [
      {
        name: 'Promenade',
        desc: "La promenade seule n'est pas soumise à l'ACACED. Cette attestation concerne la garde, l'éducation et le dressage.",
      },
      {
        name: 'Premiers secours canins',
        desc: 'Savoir réagir en cas de coupure de laisse ou malaise en balade.',
      },
      { name: 'Assurance RC Pro', desc: 'Couvre les dommages éventuels pendant la promenade.' },
    ],
    relatedMetiers: ['pet-sitter', 'educateur-canin'],
    faq: [
      {
        q: 'Combien de temps dure une promenade ?',
        a: 'En général 30, 45 ou 60 minutes selon le forfait proposé par le promeneur.',
      },
      {
        q: 'Mon chien peut-il aller en promenade de groupe ?',
        a: 'Oui si il est sociable, vacciné et identifié. Les chiens réactifs préfèrent le solo.',
      },
      {
        q: 'Le promeneur peut-il entrer chez moi ?',
        a: 'Souvent oui pour récupérer le chien. Prévoyez clés ou digicode selon l’accord.',
      },
      {
        q: 'Que faire si mon chien tire en laisse ?',
        a: 'Signalez-le au promeneur. Certains sont formés ; un éducateur peut compléter si besoin.',
      },
      {
        q: 'Promenade par tous les temps ?',
        a: 'La plupart des promeneurs sortent sauf conditions extrêmes. Vérifiez la politique du pro.',
      },
      {
        q: 'Puis-je suivre la promenade en direct ?',
        a: 'Certains pros envoient une trace GPS ou des photos en fin de sortie.',
      },
    ],
  },
  'toiletteur-canin': {
    slug: 'toiletteur-canin',
    profession: 'groomer',
    singular: 'toiletteur canin',
    plural: 'toiletteurs canins',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un toiletteur pour votre chien',
    metaTitle: 'Toiletteur canin. Réservez en ligne sur Rintintin',
    metaDescription:
      'Toilettage canin, bain, coupe ciseaux, brossage. Réservez en ligne votre toiletteur sur Rintintin. Avis clients et tarifs transparents.',
    intro:
      "Le toilettage va bien au-delà de l'esthétique : c'est un soin essentiel pour la santé du poil et de la peau. Les toiletteurs sur Rintintin adaptent leur travail à la race et au tempérament de votre chien.",
    benefits: [
      { title: 'Tous types de pelage', desc: 'Spécialistes du poil long, court, frisé ou rasoir.' },
      { title: 'Soins doux', desc: 'Bain, brossage, coupe ciseaux, démêlage avec patience.' },
      {
        title: 'En salon ou mobile',
        desc: 'Selon votre disponibilité et le confort de votre chien.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1591946614720-90a587da4a36?auto=format&fit=crop&w=1600&q=70',
    emoji: '✂️',
    processSteps: [
      {
        step: '01',
        title: 'Accueil & bilan',
        desc: 'État du pelage, peau, ongles ; choix de la prestation.',
      },
      {
        step: '02',
        title: 'Préparation',
        desc: 'Brossage, démêlage, nettoyage des yeux et oreilles.',
      },
      {
        step: '03',
        title: 'Bain & séchage',
        desc: 'Shampoing adapté, séchage progressif pour limiter le stress.',
      },
      {
        step: '04',
        title: 'Finition',
        desc: 'Coupe ciseaux ou tondeuse, griffes, parfum léger si souhaité.',
      },
    ],
    whenToConsult: [
      {
        icon: '🛁',
        title: 'Bain régulier',
        desc: 'Entretien du pelage toutes les 4 à 8 semaines selon la race.',
      },
      {
        icon: '🪮',
        title: 'Poils emmêlés',
        desc: 'Nœuds douloureux, démêlage impossible à la maison.',
      },
      {
        icon: '✂️',
        title: 'Coupe de race',
        desc: 'Caniche, bichon, spitz : entretien du standard de la race.',
      },
      {
        icon: '🌡️',
        title: 'Chaleur estivale',
        desc: 'Coupe « summer » pour les races à poil long (avec précaution).',
      },
      {
        icon: '🦴',
        title: 'Ongles trop longs',
        desc: 'Griffes qui claquent au sol ou se courbent.',
      },
      {
        icon: '👃',
        title: 'Odeurs persistantes',
        desc: 'Pelage gras ou peau qui nécessite un soin professionnel.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Spécialisation race',
        explain: 'Un toiletteur expérimenté sur votre race connaît les coupes et les précautions.',
      },
      {
        criterion: 'Salon vs camionnette',
        explain: 'Le toilettage mobile évite le transport stressant pour certains chiens.',
      },
      {
        criterion: 'Produits utilisés',
        explain: 'Shampoings doux, hypoallergéniques : demandez si votre chien a la peau sensible.',
      },
      {
        criterion: 'Gestion du stress',
        explain: 'Pauses, friandises, voix calme : essentiel pour les chiens anxieux.',
      },
    ],
    certifications: [
      {
        name: 'CTM, BTM ou CQP',
        desc: "Titres de la branche : CQP Assistant toiletteur, CTM toiletteur canin et félin, ou BTM (RNCP 38046). Il n'existe pas de CAP ni de BP toiletteur.",
      },
      { name: 'Toilettage', desc: "Le toilettage n'est pas soumis à l'ACACED." },
      { name: 'Formation races', desc: 'Stages spécifiques caniche, terrier, spitz, etc.' },
    ],
    relatedMetiers: ['osteopathe-canin', 'pet-sitter'],
    faq: [
      {
        q: 'Combien coûte un toilettage complet ?',
        a: 'Les tarifs varient selon taille, pelage et prestation. Des fourchettes indicatives sont dans le guide prix blog ; le devis du toiletteur sur sa fiche Rintintin fait foi.',
      },
      {
        q: 'À quelle fréquence toiletter un chien à poil long ?',
        a: 'Souvent toutes les 4 à 6 semaines selon race et entretien. Poil court : plutôt 8 à 12 semaines.',
      },
      {
        q: 'Toiletteur mobile ou salon : que choisir ?',
        a: 'Le mobile / domicile évite le trajet. Le salon offre un espace dédié. Choisissez selon le stress du chien et le matériel du pro.',
      },
      {
        q: 'Mon chien grogne ou tremble : que faire ?',
        a: 'Prévenez le toiletteur, demandez une séance courte, envisagez le domicile. Ne forcez pas une coupe complète la première fois.',
      },
      {
        q: 'Dois-je laver mon chien avant la séance ?',
        a: 'En général non : le bain fait partie de la prestation. Demandez au pro si un démêlage préalable aide.',
      },
      {
        q: 'Combien de temps dure un rendez-vous ?',
        a: 'Souvent 1 h à 3 h selon taille et pelage. Un chien anxieux peut commencer par une séance plus courte.',
      },
      {
        q: 'Comment réserver un toiletteur près de chez moi sur Rintintin ?',
        a: 'Ouvrez /services/toiletteur-canin, filtrez par ville, lisez les avis et réservez en ligne.',
      },
      {
        q: 'Que demander sur l’hygiène et l’assurance ?',
        a: 'Demandez désinfection, règles vaccins, et assurance. Un bon toiletteur répond sans gêne.',
      },
      {
        q: 'Faut-il des vaccins à jour ?',
        a: 'Beaucoup de salons l’exigent (rage, toux du chenil selon leur règlement). Vérifiez avant le jour J.',
      },
      {
        q: 'La tonte complète aide-t-elle en été ?',
        a: 'Pas pour toutes les races : un brossage en profondeur suffit souvent. Demandez conseil selon le type de poil.',
      },
    ],
  },
  'pension-canine': {
    slug: 'pension-canine',
    profession: 'pension',
    singular: 'pension canine',
    plural: 'pensions canines',
    preposition: 'une',
    heroTitle: 'Réserver en ligne une pension pour votre chien',
    metaTitle: 'Pension canine. Réservez en ligne sur Rintintin',
    metaDescription:
      'Trouvez une pension canine professionnelle près de chez vous pour les vacances ou déplacements. Réservation en ligne sur Rintintin.',
    intro:
      'La pension canine accueille votre chien pendant vos absences dans un cadre sécurisé. Les professionnels référencés sur Rintintin exercent dans le cadre légal en vigueur : votre chien est logé, nourri et stimulé.',
    benefits: [
      {
        title: 'Profils détaillés',
        desc: 'Consultez les photos, descriptions et avis clients de chaque pension avant de réserver.',
      },
      {
        title: 'Formats variés',
        desc: 'Pension familiale professionnelle ou structure dédiée : choisissez ce qui convient le mieux à votre chien.',
      },
      {
        title: 'Réservation en ligne',
        desc: 'Vérifiez les disponibilités et confirmez en quelques clics sur Rintintin.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1600&q=70',
    emoji: '🏡',
    processSteps: [
      {
        step: '01',
        title: 'Prise de contact',
        desc: 'Échangez sur la durée, les habitudes et les besoins de votre chien.',
      },
      {
        step: '02',
        title: 'Visite préalable',
        desc: 'Rencontre entre votre chien et la pension pour vérifier la compatibilité.',
      },
      {
        step: '03',
        title: 'Séjour',
        desc: 'Votre chien est hébergé, nourri et stimulé selon votre fiche de consignes.',
      },
      { step: '04', title: 'Retour', desc: 'Bilan du séjour et compte-rendu à votre retour.' },
    ],
    whenToConsult: [
      { icon: '✈️', title: 'Vacances', desc: 'Partez serein en sachant votre chien bien gardé.' },
      {
        icon: '💼',
        title: 'Déplacements professionnels',
        desc: 'Absences de plusieurs jours ou semaines.',
      },
      {
        icon: '🏥',
        title: 'Hospitalisation',
        desc: 'Quand vous ne pouvez pas assurer la présence au quotidien.',
      },
      {
        icon: '🐕',
        title: 'Chien sociable',
        desc: 'Les pensions en groupe conviennent aux chiens qui aiment la compagnie.',
      },
      {
        icon: '🏠',
        title: 'Chien anxieux seul',
        desc: 'La pension professionnelle rassure les chiens qui supportent mal la solitude.',
      },
      {
        icon: '💊',
        title: 'Soins particuliers',
        desc: 'Certaines pensions acceptent les chiens sous traitement médical.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Type de pension',
        explain:
          'Pension familiale professionnelle (cadre intime, peu de chiens) ou structure dédiée (personnel, espaces adaptés).',
      },
      {
        criterion: "Capacité d'accueil",
        explain:
          "Moins de chiens = plus d'attention individuelle. Demandez combien d'animaux sont accueillis.",
      },
      {
        criterion: 'Avis clients',
        explain: "Lisez les retours d'autres propriétaires sur Rintintin avant de réserver.",
      },
      {
        criterion: "Conditions d'admission",
        explain: "Vaccins à jour, tests de comportement : vérifiez les prérequis à l'avance.",
      },
    ],
    certifications: [
      {
        name: 'ACACED',
        desc: 'Obligatoire pour tout professionnel gardant des animaux à titre commercial (art. L214-6 code rural).',
      },
      {
        name: 'Premiers secours canins',
        desc: "Formation pour réagir en cas d'urgence pendant le séjour.",
      },
      { name: 'Assurance RC Pro', desc: 'Couvre les dommages éventuels pendant la garde.' },
    ],
    relatedMetiers: ['pet-sitter', 'promeneur-canin'],
    faq: [
      {
        q: 'Quelle différence entre pension et pet-sitter ?',
        a: 'Le pet-sitter garde votre chien à votre domicile ou chez lui. La pension est une structure dédiée qui accueille plusieurs chiens.',
      },
      {
        q: 'Mon chien doit-il être vacciné ?',
        a: 'Oui, les vaccins à jour (rage, toux du chenil) sont généralement exigés. Vérifiez les conditions de la pension.',
      },
      {
        q: 'Puis-je appeler pour prendre des nouvelles ?',
        a: 'La plupart des pensions envoient des nouvelles régulières. Précisez vos attentes à la réservation.',
      },
      {
        q: 'Mon chien peut-il garder sa nourriture habituelle ?',
        a: 'Oui, amenez ses croquettes pour éviter les changements alimentaires brusques.',
      },
      {
        q: 'Que faire si mon chien est malade pendant la pension ?',
        a: 'Le responsable de la pension vous contacte et accompagne votre chien chez un vétérinaire si nécessaire.',
      },
      {
        q: 'Comment préparer mon chien à la pension ?',
        a: 'Une visite préalable et quelques nuits courtes avant un long séjour permettent une adaptation progressive.',
      },
    ],
  },
  'nutritionniste-animal': {
    slug: 'nutritionniste-animal',
    profession: 'nutriotionist',
    singular: 'nutritionniste animal',
    plural: 'nutritionnistes animaux',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un nutritionniste pour votre animal',
    metaTitle: 'Nutritionniste animal. Réservez en ligne sur Rintintin',
    metaDescription:
      "Consultez un nutritionniste pour adapter l'alimentation de votre chien ou chat. Régimes, allergies, surpoids, croissance. Réservation en ligne sur Rintintin.",
    intro:
      'Un nutritionniste animal analyse les besoins spécifiques de votre chien ou chat et établit un plan alimentaire adapté : croissance, surpoids, allergies, maladies chroniques ou simple optimisation.',
    benefits: [
      {
        title: 'Bilan alimentaire complet',
        desc: 'Analyse de la ration actuelle et identification des carences ou excès.',
      },
      {
        title: 'Plan personnalisé',
        desc: "Recommandations adaptées à la race, l'âge, l'activité et l'état de santé.",
      },
      {
        title: 'Suivi dans le temps',
        desc: "Ajustements du plan selon l'évolution du poids et de la condition physique.",
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1600&q=70',
    emoji: '🥗',
    processSteps: [
      {
        step: '01',
        title: 'Bilan initial',
        desc: 'Historique alimentaire, état de santé, poids, activité physique.',
      },
      {
        step: '02',
        title: 'Analyse',
        desc: 'Évaluation des apports actuels, identification des déséquilibres.',
      },
      {
        step: '03',
        title: 'Plan sur mesure',
        desc: 'Rations, fréquences, compléments, marques recommandées.',
      },
      {
        step: '04',
        title: 'Suivi',
        desc: 'Contrôle du poids et ajustements selon les retours du propriétaire.',
      },
    ],
    whenToConsult: [
      {
        icon: '⚖️',
        title: 'Surpoids',
        desc: 'Le vétérinaire a identifié un problème de poids à corriger.',
      },
      {
        icon: '🌾',
        title: 'Allergies alimentaires',
        desc: 'Démangeaisons, problèmes digestifs, intolérance à certains ingrédients.',
      },
      {
        icon: '🐾',
        title: 'Chiot en croissance',
        desc: 'Besoin de rations adaptées pour un développement optimal.',
      },
      {
        icon: '🧓',
        title: 'Animal senior',
        desc: "Les besoins changent avec l'âge : adapter la ration évite des problèmes.",
      },
      {
        icon: '🏃',
        title: 'Chien sportif',
        desc: 'Canicross, agility, chiens de travail : besoins énergétiques élevés.',
      },
      {
        icon: '💊',
        title: 'Maladie chronique',
        desc: "Insuffisance rénale, diabète, cancer : l'alimentation fait partie du traitement.",
      },
    ],
    howToChoose: [
      {
        criterion: 'Formation vétérinaire ou spécialisée',
        explain: 'Préférez un professionnel avec une formation en nutrition animale reconnue.',
      },
      {
        criterion: 'Approche indépendante',
        explain: 'Un bon nutritionniste reste indépendant des marques de croquettes.',
      },
      {
        criterion: 'Collaboration avec le vétérinaire',
        explain: 'Pour les cas médicaux, la nutrition est un complément au traitement vétérinaire.',
      },
      {
        criterion: 'Suivi dans le temps',
        explain: 'Une consultation unique ne suffit pas : vérifiez que le pro propose un suivi.',
      },
    ],
    certifications: [
      {
        name: 'Formation en nutrition animale',
        desc: 'Diplôme universitaire, formation vétérinaire complémentaire ou certification reconnue.',
      },
      {
        name: 'ACACED',
        desc: 'Attestation recommandée pour tout professionnel travaillant avec des animaux.',
      },
      {
        name: 'Formation continue',
        desc: 'Les recommandations nutritionnelles évoluent : un bon pro se forme régulièrement.',
      },
    ],
    relatedMetiers: ['osteopathe-canin', 'educateur-canin'],
    faq: [
      {
        q: 'Quand consulter un nutritionniste animal ?',
        a: 'Transition alimentaire, ration ménagère, senior, surpoids… dès que vous voulez un cadre clair — sans remplacer le vétérinaire.',
      },
      {
        q: 'Remplace-t-il le vétérinaire ?',
        a: 'Non. Le nutritionniste conseille l’alimentation ; le véto diagnostique et soigne.',
      },
      {
        q: 'BARF / ration ménagère : que demander ?',
        a: 'Demandez l’équilibre nutritionnel, le suivi et la coopération avec le véto. Évitez les régimes internet non encadrés.',
      },
      {
        q: 'Allergies alimentaires : démarche raisonnable ?',
        a: 'Véto d’abord (plusieurs causes de démangeaisons). Puis éventuel suivi nutritionnel encadré — pas de diagnostic maison.',
      },
      {
        q: 'Chien en surpoids : objectifs réalistes ?',
        a: 'Progression lente, pas de jeûne improvisé. Cadre nutritionniste + avis médical si besoin.',
      },
      {
        q: 'Compléments : attention ?',
        a: 'Oui : évitez les allégations miracles. Validez avec nutritionniste et/ou véto avant d’empiler les produits.',
      },
      {
        q: 'Comment préparer la première consultation ?',
        a: 'Poids, âge, liste des aliments/friandises, antécédents et comptes-rendus véto utiles.',
      },
      {
        q: 'Comment réserver un nutritionniste près de chez moi ?',
        a: 'Allez sur /services/nutritionniste-animal, filtrez par zone et réservez un créneau sur Rintintin.',
      }
    ],
  },
  'communication-animale': {
    slug: 'communication-animale',
    profession: 'animal-communicator',
    singular: 'communicant animal',
    plural: 'communicants animaux',
    preposition: 'un',
    heroTitle: 'Réserver en ligne une séance de communication animale',
    metaTitle: 'Communication animale. Réservez en ligne sur Rintintin',
    metaDescription:
      'Prenez rendez-vous avec un communicant animal pour mieux comprendre votre chien ou chat. Séances en présentiel ou à distance. Réservation sur Rintintin.',
    intro:
      'La communication animale est une pratique qui vise à établir un dialogue intuitif avec votre animal. Elle est utilisée par certains propriétaires pour explorer le bien-être émotionnel de leur compagnon ou comprendre des comportements inexpliqués.',
    benefits: [
      {
        title: 'Profils détaillés',
        desc: 'Consultez la description et les avis clients de chaque communicant avant de réserver.',
      },
      {
        title: 'En présentiel ou à distance',
        desc: 'Beaucoup de séances se font par photo ou visio, sans déplacement.',
      },
      {
        title: 'Réservation en ligne',
        desc: 'Consultez les disponibilités et réservez directement sur Rintintin.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1600&q=70',
    emoji: '🌿',
    processSteps: [
      {
        step: '01',
        title: 'Échange préalable',
        desc: "Le communicant recueille une photo et le prénom de l'animal.",
      },
      {
        step: '02',
        title: 'Séance',
        desc: 'La session se déroule en présentiel ou à distance selon le professionnel.',
      },
      {
        step: '03',
        title: 'Restitution',
        desc: "Le communicant partage ses observations et réponses supposées de l'animal.",
      },
      {
        step: '04',
        title: 'Échange',
        desc: 'Discussion sur les pistes évoquées et leur pertinence pour le propriétaire.',
      },
    ],
    whenToConsult: [
      {
        icon: '❓',
        title: 'Comportement inexpliqué',
        desc: 'Changement soudain de comportement sans cause médicale identifiée.',
      },
      { icon: '😢', title: 'Deuil animal', desc: "Accompagnement lors de la perte d'un animal." },
      {
        icon: '🔄',
        title: 'Transition de vie',
        desc: "Déménagement, arrivée d'un bébé, d'un autre animal.",
      },
      {
        icon: '💬',
        title: 'Mieux comprendre son animal',
        desc: 'Curiosité sur les émotions et ressentis de son compagnon.',
      },
      {
        icon: '🌙',
        title: 'Fin de vie',
        desc: "Accompagnement lors d'une maladie grave ou d'une euthanasie.",
      },
      {
        icon: '🏠',
        title: 'Adoption récente',
        desc: "Favoriser l'intégration d'un animal dans son nouveau foyer.",
      },
    ],
    howToChoose: [
      {
        criterion: 'Expérience et références',
        explain: 'Lisez les avis clients sur Rintintin pour évaluer le sérieux du professionnel.',
      },
      {
        criterion: 'Transparence sur la pratique',
        explain:
          'Un bon communicant explique clairement sa démarche sans faire de promesses absolues.',
      },
      {
        criterion: 'Complémentarité avec le vétérinaire',
        explain:
          'La communication animale ne remplace pas un diagnostic médical ou comportemental.',
      },
      {
        criterion: 'Format de la séance',
        explain: 'Présentiel, photo ou visio : choisissez le format qui vous convient.',
      },
    ],
    certifications: [
      {
        name: 'Formation en communication animale',
        desc: 'Diverses formations existent ; renseignez-vous sur le parcours du professionnel.',
      },
      {
        name: 'Expérience pratique',
        desc: "Demandez le nombre de consultations réalisées et les retours d'autres propriétaires.",
      },
    ],
    relatedMetiers: ['comportementaliste-canin', 'osteopathe-canin'],
    faq: [
      {
        q: 'La communication animale est-elle reconnue scientifiquement ?',
        a: "Non, il n'existe pas de validation scientifique de cette pratique. Elle relève du domaine du bien-être et de l'accompagnement émotionnel.",
      },
      {
        q: "Faut-il que l'animal soit présent ?",
        a: "Pas nécessairement. Beaucoup de communicants travaillent à distance, à partir d'une photo.",
      },
      {
        q: 'Peut-on communiquer avec un animal décédé ?',
        a: 'Certains communicants proposent cet accompagnement dans un contexte de deuil.',
      },
      {
        q: 'Cela peut-il remplacer un comportementaliste ?',
        a: 'Non. Pour des troubles comportementaux, un comportementaliste ou éducateur reste indispensable.',
      },
      {
        q: 'Combien de temps dure une séance ?',
        a: 'En général entre 45 minutes et 1h30 selon le professionnel et la complexité de la demande.',
      },
      {
        q: 'Mon animal doit-il être présent pendant la séance à distance ?',
        a: 'Non, une simple photo suffit pour la plupart des séances à distance.',
      },
    ],
  },
  'loisir-canin': {
    slug: 'loisir-canin',
    profession: 'loisir-canin',
    singular: 'animateur loisir canin',
    plural: 'animateurs loisir canin',
    preposition: 'un',
    heroTitle: 'Réserver en ligne une activité de loisir canin',
    metaTitle: 'Loisir canin. Activités et sports avec votre chien sur Rintintin',
    metaDescription:
      'Agility, canicross, caninatation, flyball… Trouvez et réservez une activité canine pour dépenser votre chien et renforcer votre lien. Réservation sur Rintintin.',
    intro:
      'Les activités de loisir canin permettent de dépenser votre chien physiquement et mentalement tout en renforçant votre relation. Agility, canicross, caninatation ou jeux de pistage : il y en a pour tous les profils.',
    benefits: [
      {
        title: 'Dépense physique et mentale',
        desc: 'Un chien stimulé est un chien épanoui, moins destructeur et moins anxieux.',
      },
      {
        title: 'Renforcement du lien',
        desc: 'Les activités partagées créent une complicité unique entre chien et propriétaire.',
      },
      {
        title: 'Activités variées',
        desc: "Agility, canicross, pistage, jeux d'eau : une discipline pour chaque chien.",
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&w=1600&q=70',
    emoji: '🏅',
    processSteps: [
      {
        step: '01',
        title: "Choix de l'activité",
        desc: "L'animateur vous aide à choisir la discipline adaptée à votre chien.",
      },
      {
        step: '02',
        title: 'Évaluation initiale',
        desc: 'Test de niveau et de tempérament pour adapter les exercices.',
      },
      {
        step: '03',
        title: 'Séances',
        desc: 'Apprentissage progressif dans un cadre bienveillant et ludique.',
      },
      {
        step: '04',
        title: 'Progression',
        desc: 'Passage de niveaux, concours ou simple plaisir selon vos envies.',
      },
    ],
    whenToConsult: [
      {
        icon: '⚡',
        title: 'Chien très énergique',
        desc: 'Berger, malinois, husky : besoin de dépense intense et régulière.',
      },
      {
        icon: '🧠',
        title: 'Stimulation mentale',
        desc: 'Les jeux de flair et de pistage fatiguent autant que la course.',
      },
      {
        icon: '🤝',
        title: 'Créer du lien',
        desc: 'Activité commune pour renforcer la communication avec votre chien.',
      },
      {
        icon: '🏆',
        title: 'Compétition',
        desc: 'Agility, obéissance, flyball : pour les passionnés qui veulent aller plus loin.',
      },
      {
        icon: '🩹',
        title: 'Rééducation douce',
        desc: 'Activités adaptées pour les chiens en convalescence ou à mobilité réduite.',
      },
      {
        icon: '👶',
        title: 'Chiot en développement',
        desc: 'Les jeux structurés favorisent la socialisation et la confiance en soi.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Discipline adaptée',
        explain:
          "Chaque race et tempérament se prête mieux à certaines activités. L'animateur vous guide.",
      },
      {
        criterion: 'Méthodes positives',
        explain: 'Renforcement positif uniquement : les activités doivent rester un plaisir.',
      },
      {
        criterion: 'Taille du groupe',
        explain:
          'Cours collectifs pour la socialisation, cours individuels pour les chiens réactifs.',
      },
      {
        criterion: 'Conditions physiques du chien',
        explain:
          'Certaines activités sont déconseillées aux très jeunes chiots ou aux chiens arthritiques.',
      },
    ],
    certifications: [
      {
        name: 'Brevet fédéral',
        desc: "Certaines fédérations (SCC, CNEAC) délivrent des brevets d'animateur canin.",
      },
      {
        name: 'ACACED',
        desc: 'Attestation recommandée pour tout professionnel encadrant des chiens.',
      },
      {
        name: 'Premiers secours canins',
        desc: "Indispensable lors d'activités physiques intenses en extérieur.",
      },
    ],
    relatedMetiers: ['educateur-canin', 'promeneur-canin'],
    faq: [
      {
        q: "À quel âge peut-on commencer l'agility ?",
        a: "En général après 12-18 mois selon la race, pour laisser les articulations se développer. Des jeux d'initiation sont possibles dès 6 mois.",
      },
      {
        q: 'Mon chien doit-il déjà être obéissant ?',
        a: "Les bases (assis, stop, rappel) sont utiles mais pas indispensables. L'animateur part de là où en est votre chien.",
      },
      {
        q: 'Le canicross est-il dangereux ?',
        a: "Non si le chien est adulte, en bonne santé et que l'effort est progressif. Un bilan vétérinaire préalable est conseillé.",
      },
      {
        q: "Peut-on pratiquer avec n'importe quelle race ?",
        a: 'Oui, il existe une activité pour chaque profil. Certaines disciplines sont plus adaptées à certaines races.',
      },
      {
        q: "Faut-il s'inscrire dans un club ?",
        a: "Non, beaucoup d'animateurs proposent des séances indépendantes. Les clubs permettent de pratiquer en groupe et de participer à des concours.",
      },
      {
        q: 'Combien de séances par semaine ?',
        a: "Une à deux séances par semaine suffisent pour progresser. La régularité compte plus que l'intensité.",
      },
    ],
  },
  'osteopathe-canin': {
    slug: 'osteopathe-canin',
    profession: 'osteopath',
    singular: 'ostéopathe canin',
    plural: 'ostéopathes canins',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un ostéopathe pour votre chien',
    metaTitle: 'Ostéopathe canin. Réservez en ligne sur Rintintin',
    metaDescription:
      'Soulagez votre chien avec un ostéopathe canin : douleurs, mobilité, séniors et chiens sportifs. Prise de rendez-vous en ligne sur Rintintin.',
    intro:
      "L'ostéopathie canine est une approche douce qui agit sur la mobilité, les tensions et les douleurs de votre chien. Particulièrement précieuse pour les chiens sportifs, séniors ou en convalescence.",
    benefits: [
      {
        title: 'Techniques manuelles douces',
        desc: 'Aucune contrainte, juste des mains expertes.',
      },
      { title: 'Bilan complet', desc: 'Évaluation posturale et motrice avant chaque séance.' },
      { title: 'Suivi long-terme', desc: 'Plan de prévention pour les chiens âgés ou actifs.' },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&w=1600&q=70',
    emoji: '🤲',
    processSteps: [
      {
        step: '01',
        title: 'Anamnèse',
        desc: 'Historique médical, activité, signes observés par le propriétaire.',
      },
      {
        step: '02',
        title: 'Palpation & tests',
        desc: 'Mobilité des articulations, tension musculaire, posture.',
      },
      {
        step: '03',
        title: 'Traitement',
        desc: 'Manipulations douces pour rétablir l’équilibre corporel.',
      },
      {
        step: '04',
        title: 'Conseils post-séance',
        desc: 'Repos, étirements, fréquence des prochaines visites.',
      },
    ],
    whenToConsult: [
      {
        icon: '🦴',
        title: 'Boiterie inexpliquée',
        desc: 'Le chien boite sans blessure visible aux radiographies.',
      },
      {
        icon: '🏃',
        title: 'Chien sportif',
        desc: 'Agility, canicross : entretien et prévention des blessures.',
      },
      {
        icon: '👴',
        title: 'Chien senior',
        desc: 'Raideur, difficulté à se lever, à monter les escaliers.',
      },
      { icon: '🚗', title: 'Après un choc', desc: 'Accident, chute, freinage brutal en voiture.' },
      {
        icon: '🔄',
        title: 'Troubles chroniques',
        desc: 'Douleurs dorsales, cervicalgies, raideur persistante.',
      },
      {
        icon: '🐕',
        title: 'Changement de posture',
        desc: 'Dos voûté, refuse certaines positions, gémissements.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Inscription au registre',
        explain:
          "Vérifiez l'inscription au registre national d'aptitude, ou le tableau de l'Ordre pour un vétérinaire. Le diplôme d'école ne suffit pas.",
      },
      {
        criterion: 'Expérience espèce',
        explain:
          'Chiens et chats n’ont pas la même morphologie : privilégiez un spécialiste canin.',
      },
      {
        criterion: 'Collaboration véto',
        explain:
          'Un bon ostéopathe oriente vers le vétérinaire si une cause médicale est suspectée.',
      },
      {
        criterion: 'Approche globale',
        explain: 'Alimentation, activité, environnement : le pro doit regarder l’ensemble.',
      },
    ],
    certifications: [
      {
        name: "Registre national d'aptitude",
        desc: "Un non-vétérinaire exerce l'ostéopathie animale seulement s'il est inscrit au registre de l'Ordre des vétérinaires. Un diplôme d'école ne suffit pas.",
      },
      {
        name: 'Vétérinaire',
        desc: "Les vétérinaires inscrits à l'Ordre peuvent pratiquer ces actes.",
      },
      { name: 'Formation continue', desc: 'Sport canin, gériatrie, chiots en croissance.' },
    ],
    relatedMetiers: ['comportementaliste-canin', 'educateur-canin'],
    faq: [
      {
        q: 'Mon chien boite : ostéo ou véto d’abord ?',
        a: 'Vétérinaire d’abord si la boiterie est brutale ou très douloureuse. L’ostéo peut venir ensuite en complément, pas à la place d’un avis médical.',
      },
      {
        q: 'Combien de séances faut-il ?',
        a: 'Il n’existe pas de chiffre magique. Méfiez-vous des promesses « X séances = guéri » ; le suivi dépend du chien et de l’avis croisé avec le véto.',
      },
      {
        q: 'Faut-il une ordonnance ?',
        a: 'Demandez au praticien selon votre situation. En cas de doute médical, le vétérinaire reste la référence.',
      },
      {
        q: 'Comment se déroule une séance ?',
        a: 'Souvent observation, palpation et manipulations douces. Voir notre guide « déroulement d’une séance » — chaque pro a sa méthode.',
      },
      {
        q: 'Ostéo pour chiot ou senior ?',
        a: 'Possible selon le praticien, avec prudence accrue. Écarter d’abord une urgence médicale.',
      },
      {
        q: 'Différence ostéo / massage / kiné ?',
        a: 'Objectifs et formations diffèrent. Le massage vise surtout le bien-être ; l’ostéo une approche manuelle plus globale. Demandez la formation du pro.',
      },
      {
        q: 'Comment réserver un ostéopathe sur Rintintin ?',
        a: 'Ouvrez /services/osteopathe-canin, choisissez une zone, précisez le motif et si un véto a déjà vu le chien, puis réservez en ligne.',
      },
      {
        q: 'Quel prix indicatif ?',
        a: 'Variable selon région et durée. Pas de grille inventée ici : consultez les tarifs sur chaque fiche Rintintin.',
      }
    ],
  },
  'massage-animalier': {
    slug: 'massage-animalier',
    profession: 'massage-animalier',
    singular: 'massage animalier',
    plural: 'massages animaliers',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un massage animalier',
    metaTitle: 'Massage animalier. Réservez en ligne sur Rintintin',
    metaDescription:
      'Trouvez un praticien en massage animalier près de chez vous : détente, seniors, sport. Avis clients et réservation en ligne sur Rintintin.',
    intro:
      "Le massage animalier favorise la détente, la circulation et le bien-être de votre compagnon. Complément utile après l'effort, pour un sénior raide ou un animal stressé — sans remplacer un avis vétérinaire en cas de douleur aiguë.",
    benefits: [
      {
        title: 'Séances douces',
        desc: 'Pressions adaptées à la morphologie et à l’âge de l’animal.',
      },
      {
        title: 'Bien-être au quotidien',
        desc: 'Détente, récupération et moment privilégié avec le pro.',
      },
      {
        title: 'Près de chez vous',
        desc: 'À domicile ou en cabinet selon le praticien.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1600&q=70',
    emoji: '💆',
    processSteps: [
      {
        step: '01',
        title: 'Échange préalable',
        desc: 'Âge, activité, zones sensibles et objectifs (détente, récupération…).',
      },
      {
        step: '02',
        title: 'Observation',
        desc: 'Posture, tension musculaire et confort avant le travail.',
      },
      {
        step: '03',
        title: 'Massage',
        desc: 'Techniques manuelles progressives, adaptées à la tolérance de l’animal.',
      },
      {
        step: '04',
        title: 'Conseils',
        desc: 'Repos, hydratation et fréquence des prochaines séances.',
      },
    ],
    whenToConsult: [
      {
        icon: '😌',
        title: 'Animal stressé',
        desc: 'Anxiété légère, besoin de calme après un déménagement ou un voyage.',
      },
      {
        icon: '👴',
        title: 'Sénior raide',
        desc: 'Raideur matinale, moins d’envie de jouer, récupération plus lente.',
      },
      {
        icon: '🏃',
        title: 'Après le sport',
        desc: 'Agility, canicross : aide à la récupération musculaire.',
      },
      {
        icon: '🛋️',
        title: 'Peu actif',
        desc: 'Animal d’appartement qui a besoin d’un moment de détente tactile.',
      },
      {
        icon: '🦴',
        title: 'Confort articulaire',
        desc: 'Complément de bien-être (hors urgence véto) pour zones tendues.',
      },
      {
        icon: '❤️',
        title: 'Lien avec le pro',
        desc: 'Habituation douce avant un toilettage ou une consultation.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Formation massage animalier',
        explain: 'Demandez le cursus suivi et les espèces prises en charge.',
      },
      {
        criterion: 'Distinction ostéo / massage',
        explain:
          'Le massage vise détente et bien-être ; l’ostéopathie traite mobilité et dysfonctions.',
      },
      {
        criterion: 'Cadre de séance',
        explain: 'Au sol, sur table, à domicile : choisissez ce qui rassure votre animal.',
      },
      {
        criterion: 'Orientation véto',
        explain: 'Un pro sérieux oriente vers le vétérinaire en cas de boiterie ou douleur aiguë.',
      },
    ],
    certifications: [
      {
        name: 'Formation massage animalier',
        desc: 'Organisme reconnu avec pratique encadrée.',
      },
      {
        name: 'ACACED',
        desc: 'Souvent requis pour exercer auprès d’animaux en France.',
      },
      {
        name: 'Hygiène & sécurité',
        desc: 'Gestes adaptés, contre-indications connues, collab éventuelle avec véto.',
      },
    ],
    relatedMetiers: ['osteopathe-canin', 'toiletteur-canin'],
    faq: [
      {
        q: 'Quels bienfaits attendre d’un massage animalier ?',
        a: 'Souvent détente et confort. Ce n’est pas une guérison garantie ni un traitement médical.',
      },
      {
        q: 'Y a-t-il des contre-indications ?',
        a: 'Oui possibles (fièvre, plaie, douleur aiguë, post-op…). Demandez l’avis du vétérinaire en cas de doute.',
      },
      {
        q: 'Différence avec l’ostéopathie ?',
        a: 'Pas le même objectif ni la même formation type. Voir nos guides massage vs ostéo.',
      },
      {
        q: 'Combien de temps dure une séance ?',
        a: 'Souvent 30 à 60 minutes — confirmez avec le professionnel.',
      },
      {
        q: 'À quelle fréquence ?',
        a: 'Selon le chien et le contexte ; pas de protocole universel. Demandez au masseur.',
      },
      {
        q: 'Comment réserver un masseur animalier ?',
        a: 'Ouvrez /services/massage-animalier, lisez les avis et réservez en ligne sur Rintintin.',
      },
      {
        q: 'Mon chien boite : puis-je le faire masser ?',
        a: 'Faites voir un vétérinaire avant. Ne massez pas « par-dessus » une boiterie non explorée.',
      },
      {
        q: 'Le massage remplace-t-il l’ostéo ?',
        a: 'Non. Complémentaire éventuel, pas interchangeable.',
      }
    ],
  },
  'sport-canin': {
    slug: 'sport-canin',
    profession: 'sport-canin',
    singular: 'moniteur de sport canin',
    plural: 'moniteurs de sport canin',
    preposition: 'un',
    heroTitle: 'Réserver en ligne une séance de sport canin',
    metaTitle: 'Sport canin. Réservez une séance sur Rintintin',
    metaDescription:
      'Agility, canicross, hoopers : trouvez un moniteur de sport canin près de chez vous. Profils, avis et réservation en ligne sur Rintintin.',
    intro:
      'Quand le tour du quartier ne suffit plus, le sport canin pose un cadre : obstacles, traction, flair. Un moniteur adapte l’effort à l’âge et au corps du chien — sans en faire un concours dès la première séance.',
    benefits: [
      {
        title: 'Effort cadré',
        desc: 'Progression, pauses, surface : le pro dit ce qui est raisonnable pour votre chien.',
      },
      {
        title: 'Pas seulement de la course',
        desc: 'Agility, canicross, hoopers, pistage : une discipline selon l’énergie et le lien que vous voulez.',
      },
      {
        title: 'Créneau visible',
        desc: 'Vous voyez le lieu, la durée et le tarif sur la fiche avant de réserver.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1600&q=70',
    emoji: '🏅',
    processSteps: [
      {
        step: '01',
        title: 'État des lieux',
        desc: 'Âge, vaccins, articulations, ce que vous avez déjà tenté en promenade.',
      },
      {
        step: '02',
        title: 'Choix de la discipline',
        desc: 'Le moniteur propose un format réaliste, pas un catalogue de concours.',
      },
      {
        step: '03',
        title: 'Séance',
        desc: 'Exercices courts, consignes claires, le chien apprend sans s’épuiser.',
      },
      {
        step: '04',
        title: 'À refaire à la maison',
        desc: 'Deux ou trois gestes, pas un programme de trois heures.',
      },
    ],
    whenToConsult: [
      {
        icon: '⚡',
        title: 'Trop d’énergie',
        desc: 'Le soir, il tourne encore après une heure de marche.',
      },
      {
        icon: '🏆',
        title: 'Goût du sport',
        desc: 'Vous voulez tester l’agility ou le canicross, pas improvisé au parc.',
      },
      {
        icon: '🦴',
        title: 'Chiot en croissance',
        desc: 'Sauts et traction : à doser. Un moniteur évite de brûler les étapes.',
      },
      {
        icon: '🤝',
        title: 'Travailler à deux',
        desc: 'Le sport, c’est aussi des consignes partagées, pas seulement du cardio.',
      },
      {
        icon: '🌧️',
        title: 'Besoin d’un lieu',
        desc: 'Vous n’avez pas de terrain. Le club ou le parcours du pro sert de cadre.',
      },
      {
        icon: '🧠',
        title: 'Tête qui s’ennuie',
        desc: 'Un malinois ou un border a besoin d’un problème à résoudre, pas d’un 3e tour d’immeuble.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Discipline et niveau',
        explain: 'Demandez ce qui est proposé vraiment (initiation vs compétition) et pour quel âge.',
      },
      {
        criterion: 'Surface et matériel',
        explain: 'Herbe, tapis, harnais : un pro sérieux dit où ça se passe et ce que vous devez apporter.',
      },
      {
        criterion: 'Groupe ou solo',
        explain: 'Un chien réactif en laisse n’a rien à faire d’emblée dans un lot de six.',
      },
      {
        criterion: 'Lien avec l’éducation',
        explain: 'Le rappel et le stop aident. Ce n’est pas la même séance qu’un éducateur, mais les bases comptent.',
      },
    ],
    certifications: [
      {
        name: 'Encadrement sportif',
        desc: 'Demandez le cursus (club, fédération, formation d’animateur) plutôt qu’un titre vague.',
      },
      {
        name: 'ACACED',
        desc: 'Souvent requis en France pour encadrer des chiens à titre professionnel.',
      },
      {
        name: 'Premiers secours',
        desc: 'Utile dès qu’il y a effort, chaleur ou obstacles.',
      },
    ],
    relatedMetiers: ['loisir-canin', 'educateur-canin'],
    faq: [
      {
        q: 'Quelle différence entre loisir canin et sport canin ?',
        a: 'Le loisir reste plus ludique. Le sport vise un effort plus structuré (parcours, traction, enchaînements). Sur Rintintin les deux métiers existent : lisez la fiche du pro.',
      },
      {
        q: 'Mon chiot peut-il faire de l’agility ?',
        a: 'Les sauts répétés attendent souvent la maturité osseuse. Beaucoup de moniteurs proposent d’abord du sol, du flair, du contrôle. Demandez l’âge minimum.',
      },
      {
        q: 'Faut-il déjà un bon rappel ?',
        a: 'Ça aide. Ce n’est pas toujours un prérequis. Un chien qui part au moindre écureuil aura besoin d’un cadre plus calme, parfois d’abord avec un éducateur.',
      },
      {
        q: 'C’est dangereux, le canicross ?',
        a: 'Ça dépend du chien, de la chaleur et de la progression. Un bilan véto avant un effort régulier est raisonnable. Personne ne devrait vous promettre un chrono dès la première sortie.',
      },
      {
        q: 'Combien de séances pour y prendre goût ?',
        a: 'Pas de chiffre magique. Une séance découverte avant un forfait évite d’acheter dix cours pour un chien qui déteste les obstacles.',
      },
      {
        q: 'Où se passent les séances ?',
        a: 'Club, terrain, forêt, à domicile selon le pro. C’est indiqué sur la fiche — vérifiez avant de réserver.',
      },
      {
        q: 'Comment réserver un moniteur de sport canin ?',
        a: 'Ouvrez /services/sport-canin, comparez les profils, lisez les avis et réservez un créneau sur Rintintin.',
      },
      {
        q: 'Mon chien boite après le sport : je reviens quand même ?',
        a: 'Non. Véto d’abord. Le sport ne se met pas par-dessus une boiterie.',
      },
    ],
  },
  'naturopathe': {
    slug: 'naturopathe',
    profession: 'naturopathe',
    singular: 'naturopathe animalier',
    plural: 'naturopathes animaliers',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un naturopathe animalier',
    metaTitle: 'Naturopathe animalier. Réservez en ligne sur Rintintin',
    metaDescription:
      'Hygiène de vie, confort du quotidien : trouvez un naturopathe animalier près de chez vous. Avis et réservation en ligne sur Rintintin — sans remplacer le vétérinaire.',
    intro:
      'Un naturopathe animalier travaille sur le confort du quotidien : alimentation, stress, terrain. C’est un complément, pas un cabinet médical. Fièvre, douleur, plaie, chien qui s’effondre : le vétérinaire d’abord.',
    benefits: [
      {
        title: 'Temps pour raconter le quotidien',
        desc: 'Sommeil, croquettes, sorties, déménagement : le pro part de votre maison, pas d’un protocole tout fait.',
      },
      {
        title: 'Complément, pas substitut',
        desc: 'Un naturopathe sérieux oriente vers le véto dès qu’un signe médical apparaît.',
      },
      {
        title: 'Fiche avant le RDV',
        desc: 'Vous lisez l’approche et les tarifs sur le profil, puis vous réservez le créneau.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&w=1600&q=70',
    emoji: '🌿',
    processSteps: [
      {
        step: '01',
        title: 'Entretien',
        desc: 'Historique, alimentation actuelle, traitements en cours — sans tout arrêter dans votre dos.',
      },
      {
        step: '02',
        title: 'Observation',
        desc: 'Poil, selles, énergie, stress : ce que vous voyez au quotidien.',
      },
      {
        step: '03',
        title: 'Pistes concrètes',
        desc: 'Ajustements possibles, toujours compatibles avec l’avis vétérinaire.',
      },
      {
        step: '04',
        title: 'Suivi',
        desc: 'Un point plus tard si besoin. Pas de « cure miracle » vendue d’avance.',
      },
    ],
    whenToConsult: [
      {
        icon: '🍽️',
        title: 'Confort digestif',
        desc: 'Selles irrégulières, croquettes qui ne passent plus — après un avis véto si ça dure.',
      },
      {
        icon: '😌',
        title: 'Chien tendu',
        desc: 'Déménagement, orages, nouveau bébé : pistes de confort, pas une promesse de calme en 7 jours.',
      },
      {
        icon: '👴',
        title: 'Senior qui ralentit',
        desc: 'Moins d’allant, récupération lente. Le véto reste la référence pour la douleur.',
      },
      {
        icon: '🌱',
        title: 'Vous voulez moins de « produits au hasard »',
        desc: 'Huiles, plantes, compléments : mieux vaut un interlocuteur que le rayon du magasin.',
      },
      {
        icon: '🔄',
        title: 'Après un traitement',
        desc: 'Le quotidien à reconstruire, sans remplacer le suivi médical.',
      },
      {
        icon: '❓',
        title: 'Vous hésitez avec la nutrition',
        desc: 'Le nutritionniste chiffre la ration. Le naturopathe parle plus large (hygiène de vie). Les deux ne se confondent pas.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Limites clairement dites',
        explain: 'Méfiance si l’on promet de guérir une maladie ou d’arrêter un traitement véto.',
      },
      {
        criterion: 'Formation affichée',
        explain: 'Demandez le cursus et les espèces prises en charge.',
      },
      {
        criterion: 'Lien avec le vétérinaire',
        explain: 'Un pro sérieux ne se met pas en travers d’un diagnostic.',
      },
      {
        criterion: 'Ce qui est vendu',
        explain: 'Conseil vs pots à emporter : lisez la fiche, posez la question avant le RDV.',
      },
    ],
    certifications: [
      {
        name: 'Formation naturopathie animale',
        desc: 'Organisme identifiable, pratique encadrée. Ce n’est pas un diplôme d’État de vétérinaire.',
      },
      {
        name: 'ACACED',
        desc: 'Souvent requis pour exercer auprès d’animaux en France.',
      },
      {
        name: 'Hygiène et contre-indications',
        desc: 'Plantes et compléments ne sont pas anodins. Un pro les connaît.',
      },
    ],
    relatedMetiers: ['nutritionniste-animal', 'osteopathe-canin'],
    faq: [
      {
        q: 'La naturopathie remplace-t-elle le vétérinaire ?',
        a: 'Non. Jamais. Douleur, fièvre, sang, animal abattu : véto. Le naturopathe intervient en complément de confort.',
      },
      {
        q: 'Quelle différence avec un nutritionniste animal ?',
        a: 'Le nutritionniste travaille surtout la ration et les apports. Le naturopathe parle hygiène de vie plus large. Choisissez selon la question que vous posez.',
      },
      {
        q: 'Puis-je arrêter un traitement grâce à une plante ?',
        a: 'Non sans l’avis du vétérinaire qui suit votre chien. Personne sur Rintintin ne devrait vous le demander.',
      },
      {
        q: 'Comment se passe une première séance ?',
        a: 'Surtout de l’échange, parfois observation du chien. Durée et lieu sont sur la fiche du pro.',
      },
      {
        q: 'Est-ce utile pour un chiot en pleine forme ?',
        a: 'Pas forcément. Beaucoup de propriétaires viennent pour un senior, un stress, un confort digestif déjà vu au véto.',
      },
      {
        q: 'Les compléments sont-ils inclus ?',
        a: 'Ça dépend du pro. Demandez si le tarif est le conseil seul ou des produits en plus.',
      },
      {
        q: 'Comment réserver un naturopathe animalier ?',
        a: 'Ouvrez /services/naturopathe, lisez les avis, précisez les traitements en cours, réservez en ligne.',
      },
      {
        q: 'Mon chien boite : naturopathe ou ostéo ?',
        a: 'Véto d’abord. Ensuite ostéo ou naturopathe selon l’avis médical, pas l’inverse.',
      },
    ],
  },
  'taxi-animalier': {
    slug: 'taxi-animalier',
    profession: 'taxi-animalier',
    singular: 'taxi animalier',
    plural: 'taxis animaliers',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un taxi animalier',
    metaTitle: 'Taxi animalier. Transport de votre animal sur Rintintin',
    metaDescription:
      'Gare, clinique, élevage : trouvez un taxi animalier près de chez vous. Trajet encadré, avis et réservation en ligne sur Rintintin.',
    intro:
      'Vous n’avez pas de voiture adaptée, ou le trajet vers la clinique vous stresse autant que le chien. Un taxi animalier prend le relais : cage, hamac, pauses — ce n’est ni un pet-sitter ni une ambulance.',
    benefits: [
      {
        title: 'Trajet dédié',
        desc: 'Le chien n’est pas un bagage sur le siège arrière d’un VTC classique.',
      },
      {
        title: 'Vous restez au bureau',
        desc: 'Clinique, gare, aéroport, élevage : vous décrivez le point A et le point B.',
      },
      {
        title: 'Conditions sur la fiche',
        desc: 'Taille, cage, animaux multiples : lisez avant de réserver, pas sur le trottoir.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?auto=format&fit=crop&w=1600&q=70',
    emoji: '🚗',
    processSteps: [
      {
        step: '01',
        title: 'Le trajet',
        desc: 'Adresses, horaires, cage ou laisse, chien seul ou à plusieurs.',
      },
      {
        step: '02',
        title: 'Le briefing',
        desc: 'Harnais, muselière si besoin, contact véto, ce qu’il a le droit de manger en route.',
      },
      {
        step: '03',
        title: 'La prise en charge',
        desc: 'Accueil calme, installation, départ. Vous n’avez pas à « gérer le chauffeur ».',
      },
      {
        step: '04',
        title: 'L’arrivée',
        desc: 'Remise à la personne indiquée (clinique, sitter, vous). Un message de fin de course, c’est mieux qu’un silence.',
      },
    ],
    whenToConsult: [
      {
        icon: '🏥',
        title: 'RDV véto',
        desc: 'Vous ne pouvez pas quitter le travail, le chien doit y aller quand même.',
      },
      {
        icon: '🚂',
        title: 'Gare ou aéroport',
        desc: 'Correspondance, cage soute, timing serré.',
      },
      {
        icon: '🏡',
        title: 'Vers le sitter ou la pension',
        desc: 'Le gardien est à 40 km et vous n’avez pas de coffre.',
      },
      {
        icon: '🐕',
        title: 'Chien qui vomit en voiture',
        desc: 'Un pro habitué, pauses, aération : ça n’efface pas le mal des transports, ça l’encadre.',
      },
      {
        icon: '📦',
        title: 'Déménagement',
        desc: 'Le camion est plein. Le chien voyage à part, sans le stress du hayon.',
      },
      {
        icon: '⏰',
        title: 'Horaire impossible',
        desc: 'Clinique à l’ouverture, vous êtes déjà au bureau.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Véhicule et contention',
        explain: 'Cage, grille, hamac, climatisation. Un chien anxieux n’a pas les mêmes besoins qu’un senior calme.',
      },
      {
        criterion: 'Assurance et identification',
        explain: 'Demandez ce qui est prévu si le chien se blesse ou si le RDV déborde.',
      },
      {
        criterion: 'Seul ou partagé',
        explain: 'Un trajet partagé coûte souvent moins cher, mais ajoute des arrêts et des odeurs d’autres chiens.',
      },
      {
        criterion: 'Ce n’est pas une urgence médicale',
        explain: 'Détresse respiratoire, hémorragie, choc : ce n’est pas un taxi, c’est le véto / les pompiers.',
      },
    ],
    certifications: [
      {
        name: 'Transport d’animaux vivants',
        desc: 'Selon le trajet (pro, distance), des règles s’appliquent. Demandez au chauffeur comment il est déclaré.',
      },
      {
        name: 'ACACED',
        desc: 'Souvent demandé pour manipuler des chiens à titre professionnel en France.',
      },
      {
        name: 'Assurance',
        desc: 'Vérifiez ce qui est couvert pendant le trajet, pas seulement la carte grise.',
      },
    ],
    relatedMetiers: ['pet-sitter', 'pension-canine'],
    faq: [
      {
        q: 'Le taxi animalier est-il un pet-sitter ?',
        a: 'Non. Le sitter garde. Le taxi déplace. Si vous partez une semaine, vous avez besoin d’une garde, pas seulement d’un trajet.',
      },
      {
        q: 'Puis-je monter avec mon chien ?',
        a: 'Parfois oui, parfois le service est « animal seul ». C’est sur la fiche — posez la question avant.',
      },
      {
        q: 'Comment se passe un chien craintif ?',
        a: 'Briefing, muselière si le pro la demande, pas de forçage dans le coffre. Un chien qui mord sous la panique n’est pas « méchant » : il faut le dire avant.',
      },
      {
        q: 'C’est adapté à un chat ou un NAC ?',
        a: 'Certains taxis le font, d’autres non. Filtrez et lisez la fiche espèce.',
      },
      {
        q: 'Que se passe-t-il si le RDV véto retarde ?',
        a: 'Demandez la politique d’attente. Ce n’est pas toujours inclus dans le tarif affiché.',
      },
      {
        q: 'Le chauffeur donne-t-il des médicaments ?',
        a: 'En général non. Le transport n’est pas un acte médical.',
      },
      {
        q: 'Comment réserver un taxi animalier ?',
        a: 'Ouvrez /services/taxi-animalier, indiquez les adresses et l’horaire, réservez en ligne sur Rintintin.',
      },
      {
        q: 'Mon chien est blessé : taxi ou urgence ?',
        a: 'Urgence vétérinaire. Un taxi n’est pas une ambulance.',
      },
    ],
  },
  'photographe-animalier': {
    slug: 'photographe-animalier',
    profession: 'photographe-animalier',
    singular: 'photographe animalier',
    plural: 'photographes animaliers',
    preposition: 'un',
    heroTitle: 'Réserver en ligne un photographe animalier',
    metaTitle: 'Photographe animalier. Séance photo sur Rintintin',
    metaDescription:
      'Portrait de votre chien, à la maison ou en extérieur. Trouvez un photographe animalier, lisez les avis et réservez en ligne sur Rintintin.',
    intro:
      'Vous voulez autre chose que la photo floue du dimanche. Un photographe animalier connaît le timing d’un chien impatient, le lieu qui le rassure, et le temps qu’il faut avant que ça bascule en « on rentre ».',
    benefits: [
      {
        title: 'Le chien mène le rythme',
        desc: 'Séance courte, pauses, friandises : pas une pose figée de vingt minutes.',
      },
      {
        title: 'Lieu qui lui va',
        desc: 'Jardin, forêt, salon. Un studio froid n’est pas obligatoire.',
      },
      {
        title: 'Livraison claire',
        desc: 'Nombre de photos, délais, droits : lisez la fiche avant de payer une galerie surprise.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1600&q=70',
    emoji: '📷',
    processSteps: [
      {
        step: '01',
        title: 'Brief',
        desc: 'Usage (famille, deuil, élevage), lieu, nombre de chiens, ce que vous ne voulez pas.',
      },
      {
        step: '02',
        title: 'Préparation',
        desc: 'Toilettage léger si besoin, laisse de secours, jouet. Pas un shooting mode.',
      },
      {
        step: '03',
        title: 'Séance',
        desc: 'Le photographe guide, vous tenez ou vous vous écartez. Le chien a le droit de se lasser.',
      },
      {
        step: '04',
        title: 'Galerie',
        desc: 'Choix, retouches, fichiers. Les délais sont ceux du pro, pas un standard inventé.',
      },
    ],
    whenToConsult: [
      {
        icon: '🎂',
        title: 'Un âge qui passe',
        desc: 'Chiot, senior, « avant qu’il ralentisse trop ».',
      },
      {
        icon: '👨‍👩‍👧',
        title: 'Photo de famille',
        desc: 'Humains + chien, sans que ça parte en squats interminables.',
      },
      {
        icon: '🏡',
        title: 'Chez vous',
        desc: 'Le canapé, le jardin : l’endroit où il est vraiment lui.',
      },
      {
        icon: '🌳',
        title: 'En extérieur',
        desc: 'Forêt, plage, parc un mardi matin, pas le marché du samedi.',
      },
      {
        icon: '✂️',
        title: 'Après le toiletteur',
        desc: 'Poil net, séance dans la foulée si le chien n’est pas épuisé.',
      },
      {
        icon: '🤍',
        title: 'Souvenir',
        desc: 'Maladie, grand âge : un portrait posé, sans en faire un discours.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Portfolio chiens réels',
        explain: 'Regardez des chiens qui bougent, pas seulement des poses studio.',
      },
      {
        criterion: 'Durée et livrables',
        explain: '45 min ou 2 h, 15 photos ou 80 : ça change le prix. C’est écrit ou ça s’écrit avant.',
      },
      {
        criterion: 'Chien réactif ou craintif',
        explain: 'Dites-le. Un flash, un parc bondé ou un assistant trop brusque ruinent la séance.',
      },
      {
        criterion: 'Droits d’utilisation',
        explain: 'Réseaux, tirage, élevage : demandez ce qui est inclus.',
      },
    ],
    certifications: [
      {
        name: 'Statut professionnel',
        desc: 'Facture, assurance : un shooting n’est pas un copain avec un téléphone.',
      },
      {
        name: 'Assurance',
        desc: 'Utile dès qu’on entre chez vous ou qu’on travaille en bord de route.',
      },
      {
        name: 'Habitude animale',
        desc: 'Pas un diplôme unique. Le portfolio et la façon de parler du chien suffisent souvent à vous faire une idée.',
      },
    ],
    relatedMetiers: ['toiletteur-canin', 'loisir-canin'],
    faq: [
      {
        q: 'Faut-il toiletter le chien avant ?',
        a: 'Un brossage aide. Un toilettage complet n’est utile que si le chien le vit bien — sinon vous photographiez un animal épuisé.',
      },
      {
        q: 'Combien de temps dure une séance ?',
        a: 'Souvent 30 à 90 minutes. Un chiot ou un senior tient moins longtemps. Confirmez avec le pro.',
      },
      {
        q: 'Puis-je rester hors cadre ?',
        a: 'Oui, souvent. Certains chiens se figent sans vous, d’autres ne regardent que vous. Dites ce que vous voulez.',
      },
      {
        q: 'Et s’il pleut ?',
        a: 'Report, abri, séance indoor : c’est la règle du photographe. Lisez les conditions d’annulation.',
      },
      {
        q: 'Les photos sont-elles retouchées ?',
        a: 'Oui en général, dans une mesure indiquée. Méfiance si « 200 photos HD livrées le soir même » sans détail.',
      },
      {
        q: 'Plusieurs chiens, un chat ?',
        a: 'Possible selon le pro. Deux chiens qui se disputent le jouet, ça double le temps.',
      },
      {
        q: 'Comment réserver un photographe animalier ?',
        a: 'Ouvrez /services/photographe-animalier, comparez les portfolios et réservez un créneau sur Rintintin.',
      },
      {
        q: 'Le photographe publie-t-il mon chien sur Instagram ?',
        a: 'Seulement si vous êtes d’accord. Demandez-le avant la séance.',
      },
    ],
  },
  'secourisme-animalier': {
    slug: 'secourisme-animalier',
    profession: 'secourisme-animalier',
    singular: 'formation de secourisme animalier',
    plural: 'formations de secourisme animalier',
    preposition: 'une',
    heroTitle: 'Réserver une formation de secourisme animalier',
    metaTitle: 'Secourisme animalier. Stages près de chez vous sur Rintintin',
    metaDescription:
      'Apprendre les gestes d’attente avant le véto : trouvez un stage de secourisme animalier, lisez les avis et réservez en ligne sur Rintintin.',
    intro:
      'Une plaie à la balade, une convulsion un dimanche, un étouffement avec un jouet : les minutes avant le vétérinaire comptent. Un stage de secourisme animalier vous apprend des gestes d’attente — ce n’est pas un service d’urgence, et ça ne remplace pas la clinique.',
    benefits: [
      {
        title: 'Gestes, pas de théorie floue',
        desc: 'Position latérale, saignement, coup de chaleur : vous répétez sur mannequin ou peluche, selon le formateur.',
      },
      {
        title: 'Savoir quand ne rien faire',
        desc: 'Un bon stage dit aussi ce qui aggrave. Forcer, donner un médicament « au cas où », attendre trop longtemps.',
      },
      {
        title: 'Date et lieu clairs',
        desc: 'Atelier d’une demi-journée ou module plus long : c’est sur la fiche, vous réservez le créneau.',
      },
    ],
    heroImage:
      'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1600&q=70',
    emoji: '🩹',
    processSteps: [
      {
        step: '01',
        title: 'Le groupe',
        desc: 'Niveau débutant, nombre de places, chiens autorisés ou non en salle.',
      },
      {
        step: '02',
        title: 'Le déroulé',
        desc: 'Démo, pratique, questions. Vous repartez avec une liste, pas un diplôme de soignant.',
      },
      {
        step: '03',
        title: 'Les cas',
        desc: 'Étouffement, plaie, coup de chaleur, accident de voiture — selon le programme du pro.',
      },
      {
        step: '04',
        title: 'Après',
        desc: 'Numéros d’urgence véto de votre zone, trousse à revoir à la maison.',
      },
    ],
    whenToConsult: [
      {
        icon: '🌲',
        title: 'Vous marchez souvent loin',
        desc: 'Forêt, sentier, plage : le véto n’est pas au prochain feu rouge.',
      },
      {
        icon: '👶',
        title: 'Chiot à la maison',
        desc: 'Jouets, câbles, premier été chaud.',
      },
      {
        icon: '🏅',
        title: 'Sport ou loisir',
        desc: 'Agility, canicross : un formateur rappelle les gestes après un choc.',
      },
      {
        icon: '🏡',
        title: 'Garde d’autres chiens',
        desc: 'Vous sittez, vous accueillez : savoir réagir rassure aussi les propriétaires.',
      },
      {
        icon: '☀️',
        title: 'Canicule',
        desc: 'Le coup de chaleur arrive vite. Mieux vaut l’avoir vu expliqué une fois.',
      },
      {
        icon: '🚗',
        title: 'Vous roulez avec lui',
        desc: 'Freinage, cage mal calée, premier secours sur bas-côté.',
      },
    ],
    howToChoose: [
      {
        criterion: 'Programme écrit',
        explain: 'Demandez ce qui est couvert (et ce qui ne l’est pas). Un titre « secourisme » ne dit rien à lui seul.',
      },
      {
        criterion: 'Pratique vs diapo',
        explain: 'Une matinée de slides sans mise en situation sert peu le jour où les mains tremblent.',
      },
      {
        criterion: 'Public visé',
        explain: 'Propriétaires, sitters, éducateurs : le niveau n’est pas le même.',
      },
      {
        criterion: 'Pas une urgence',
        explain: 'Si votre chien est en détresse maintenant, vous n’avez pas besoin d’un stage. Vous avez besoin d’un vétérinaire.',
      },
    ],
    certifications: [
      {
        name: 'Formateur identifié',
        desc: 'Parcours (ASV, formateur premiers secours animaliers, pompier, etc.) : demandez-le, comparez.',
      },
      {
        name: 'Attestation de stage',
        desc: 'Souvent une attestation de présence, pas un diplôme d’État. Utile, pas magique.',
      },
      {
        name: 'Mise à jour',
        desc: 'Les gestes évoluent. Un stage suivi il y a dix ans mérite parfois un rappel.',
      },
    ],
    relatedMetiers: ['educateur-canin', 'loisir-canin'],
    faq: [
      {
        q: 'Le secourisme animalier remplace-t-il le vétérinaire ?',
        a: 'Non. C’est l’attente : protéger, limiter les dégâts, filer à la clinique. Pas soigner à la maison.',
      },
      {
        q: 'Dois-je venir avec mon chien ?',
        a: 'Parfois interdit (stress, hygiène), parfois utile pour des démos calmes. C’est sur la fiche.',
      },
      {
        q: 'C’est utile si je vis en ville ?',
        a: 'Oui. Un étouffement ou un coup de chaleur n’attend pas la campagne.',
      },
      {
        q: 'Quelle durée prévoir ?',
        a: 'Souvent une demi-journée à une journée. Les formats longs existent. Lisez la fiche.',
      },
      {
        q: 'Y a-t-il un examen ?',
        a: 'Rarement un examen officiel. Plutôt de la pratique et une attestation. Méfiance vis-à-vis des « diplômes » vendus trop vite.',
      },
      {
        q: 'Je suis pet-sitter : est-ce pour moi ?',
        a: 'Oui, souvent. Mentionnez-le au formateur : les cas (clés, chien d’un autre) changent un peu les exemples.',
      },
      {
        q: 'Comment réserver un stage de secourisme animalier ?',
        a: 'Ouvrez /services/secourisme-animalier, choisissez une date et réservez en ligne sur Rintintin.',
      },
      {
        q: 'Mon chien est en train de mal aller : je réserve un stage ?',
        a: 'Non. Véto, garde nationale selon le cas. Le stage, c’est pour avant.',
      },
    ],
  },
};

export type MetierStructure = {
  slug: string;
  profession: string;
  heroImage: string;
  emoji: string;
  relatedMetiers: string[];
};

export type MetierContentFields = {
  singular: string;
  plural: string;
  preposition: string;
  heroTitle: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  benefits: { title: string; desc: string }[];
  processSteps: ProcessStep[];
  whenToConsult: WhenItem[];
  howToChoose: ChooseItem[];
  certifications: CertItem[];
  faq: FaqItem[];
};

/** Champs non traduits (structure) pour useMetierContent + i18n metiers.json */
export const METIERS_REGISTRY: Record<string, MetierStructure> = Object.fromEntries(
  Object.entries(METIERS_DATA).map(([slug, data]) => [
    slug,
    {
      slug: data.slug,
      profession: data.profession,
      heroImage: data.heroImage,
      emoji: data.emoji,
      relatedMetiers: data.relatedMetiers,
    },
  ])
);

export const METIER_SLUGS = Object.keys(METIERS_DATA);

export type CityCard = {
  name: string;
  slug: string;
  photo: string;
};

export function slugifyCity(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getCityBySlug(slug: string): CityCard | undefined {
  return CITY_CARDS.find(city => city.slug === slug);
}

// URLs CDN Unsplash vérifiées (HTTP 200, photo de la bonne ville), voir résolution via
// `https://unsplash.com/photos/{slug}/download?w=900` qui redirige vers `images.unsplash.com/photo-...`.
export const CITY_CARDS: CityCard[] = [
  {
    name: 'Paris',
    slug: 'paris',
    photo:
      'https://images.unsplash.com/photo-1768295981746-80faad1549a3?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Marseille',
    slug: 'marseille',
    photo:
      'https://images.unsplash.com/photo-1756302220296-18a9d20a6d5a?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Lyon',
    slug: 'lyon',
    photo:
      'https://images.unsplash.com/photo-1682005363584-dc9f8e38a11e?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Toulouse',
    slug: 'toulouse',
    photo:
      'https://images.unsplash.com/photo-1650707199496-b02442055332?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Bordeaux',
    slug: 'bordeaux',
    photo:
      'https://images.unsplash.com/photo-1618634494100-d983a1020b36?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Nice',
    slug: 'nice',
    photo:
      'https://images.unsplash.com/photo-1758541331151-137511d8f08c?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Lille',
    slug: 'lille',
    photo:
      'https://images.unsplash.com/photo-1709486815648-e34d369d7c6b?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Nantes',
    slug: 'nantes',
    photo:
      'https://images.unsplash.com/photo-1513759338966-5de23c844b3a?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Strasbourg',
    slug: 'strasbourg',
    photo:
      'https://images.unsplash.com/photo-1577333880314-f0bebfd2f08e?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Montpellier',
    slug: 'montpellier',
    photo:
      'https://images.unsplash.com/photo-1701605919891-1824e8dae598?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Rennes',
    slug: 'rennes',
    photo:
      'https://images.unsplash.com/photo-1654332127243-5c96057f82b9?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Grenoble',
    slug: 'grenoble',
    photo:
      'https://images.unsplash.com/photo-1570991607967-d837f86fe62e?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Rouen',
    slug: 'rouen',
    photo:
      'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Dijon',
    slug: 'dijon',
    photo:
      'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Tours',
    slug: 'tours',
    photo:
      'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Angers',
    slug: 'angers',
    photo:
      'https://images.unsplash.com/photo-1473951574080-01b059a01522?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Reims',
    slug: 'reims',
    photo:
      'https://images.unsplash.com/photo-1524396307663-2a941e31c034?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Toulon',
    slug: 'toulon',
    photo:
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Clermont-Ferrand',
    slug: 'clermont-ferrand',
    photo:
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=900&q=70',
  },
  {
    name: 'Saint-Étienne',
    slug: 'saint-etienne',
    photo:
      'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=70',
  },
];

export const CITY_SLUGS = CITY_CARDS.map(city => city.slug);
